import { useCallback, useMemo, useRef, useState } from 'react'
import { createRefreshQueue, REFRESH_MS } from '../auth/refreshQueue'
import { listInvitations, type Invitation, type Outcome } from '../data/members'

export type InvitationsState = { phase: 'loading' } | { phase: 'ready'; items: Invitation[] } | { phase: 'failed' }

export interface Invitations {
  state: InvitationsState
  /** Reads the list; resolves with the items it applied, or `null` when it applied none. */
  refetch: () => Promise<Invitation[] | null>
  /** A settled Accept, Decline or terminal refusal: drops the row now, then reads again. */
  settle: (id: string) => void
  /** Back to `loading`, with every read in flight dropped: the session ended. */
  reset: () => void
}

type Read = { revision: number; outcome: Outcome<Invitation[]> }

/**
 * The signed-in user's received invitations (GRO-106 plan, Task 20), one list for the
 * session. AuthBoundary owns it, and every reader takes it from there: the cards, the
 * header count, the avatar dot and the switcher line.
 *
 * Reads run one at a time on the refresh queue. Each captures the revision when it starts,
 * and every settled mutation advances the revision, so a read that started before a
 * mutation never restores what the mutation removed. A failed read keeps a `ready` list
 * and leaves any other state `failed`.
 */
export function useInvitations(): Invitations {
  const [state, setState] = useState<InvitationsState>({ phase: 'loading' })
  const revision = useRef(0)

  const queue = useMemo(() => createRefreshQueue<Read, Invitation[] | 'dropped' | 'failed'>({
    request: async () => {
      const captured = revision.current
      return { revision: captured, outcome: await listInvitations() }
    },
    apply: ({ revision: captured, outcome }) => {
      if (captured !== revision.current || outcome.status === 'stale' || outcome.status === 'expired') return 'dropped'
      if (outcome.status !== 'ok') {
        setState((current) => (current.phase === 'ready' ? current : { phase: 'failed' }))
        return 'failed'
      }
      setState({ phase: 'ready', items: outcome.body })
      return outcome.body
    },
    deadlineMs: REFRESH_MS,
  }), [])

  const refetch = useCallback(async () => {
    const answer = await queue()
    if (answer === null) setState((current) => (current.phase === 'ready' ? current : { phase: 'failed' }))
    return Array.isArray(answer) ? answer : null
  }, [queue])

  const settle = useCallback((id: string) => {
    revision.current += 1
    setState((current) => (current.phase === 'ready' ? { phase: 'ready', items: current.items.filter((i) => i.id !== id) } : current))
    void refetch()
  }, [refetch])

  const reset = useCallback(() => {
    revision.current += 1
    setState({ phase: 'loading' })
  }, [])

  return { state, refetch, settle, reset }
}
